type Suggestion = { label: string; value: string };

class Autocomplete {
  private input: HTMLInputElement;
  private hiddenField: HTMLInputElement | null;
  private suggestionsContainer: HTMLUListElement;

  constructor(input: HTMLInputElement) {
    this.input = input;
    this.hiddenField = this.findHiddenField(input); // Dynamically find the hidden field
    this.suggestionsContainer = this.createSuggestionsContainer();
    this.initialize();
  }

  private findHiddenField(input: HTMLInputElement): HTMLInputElement | null {
    let parent = input.parentElement;
    while (parent) {
      const hiddenField = parent.querySelector('input[type="hidden"]');
      if (hiddenField) {
        return hiddenField as HTMLInputElement;
      }
      parent = parent.parentElement; // Move to the parent element
    }
    return null; // Return null if no hidden input is found in the parent chain
  }

  private createSuggestionsContainer(): HTMLUListElement {
    const ul = document.createElement("ul");
    ul.className = "autocomplete-suggestions visually-hidden";
    document.body.appendChild(ul);
    return ul;
  }

  private initialize(): void {
    // Add autocomplete="off" to the input element
    this.input.setAttribute("autocomplete", "off");

    this.input.addEventListener("input", () => this.onInput());
    this.input.addEventListener("focus", () => this.positionSuggestionsContainer());
    document.addEventListener("click", (e) => this.onClickOutside(e));
  }

  private async onInput(): Promise<void> {
    const query = this.input.value.trim();

    if (query.length === 0) {
      this.clearSuggestions();
      return;
    }

    try {
      const value = this.input.getAttribute("data-wf-autocomplete")!;
      const suggestions = await this.fetchSuggestions(query, value);
      this.displaySuggestions(suggestions);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
    }
  }

  private async fetchSuggestions(query: string, value: string): Promise<Suggestion[]> {
    // Define endpoint and response function based on the autocomplete attribute value
    let endpoint: string | null = null;
    let responseFunc: ((data: any) => Suggestion[]) | null = null;

    if (value === "icd-10-data") {
      endpoint = "/referral/icd-10-data";
      responseFunc = (data: { key: string; value: string }[]): Suggestion[] =>
        data.map(({ key, value }) => ({
          label: value,  // The 'value' field is the label/description
          value: key,    // The 'key' field is the code
        }));
    }

    if (!endpoint || !responseFunc) {
      throw new Error(`No endpoint or response function defined for autocomplete type: ${value}`);
    }

    // Fetch the data
    const response = await fetch(`${endpoint}?term=${encodeURIComponent(query)}`);
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    // Process the data using the response function
    const data = await response.json();
    return responseFunc(data);
  }

  private displaySuggestions(suggestions: Suggestion[]): void {
    this.clearSuggestions();

    if (suggestions.length === 0) {
      this.suggestionsContainer.classList.add("visually-hidden");
      return;
    }

    const fragment = document.createDocumentFragment();

    suggestions.forEach((suggestion) => {
      const li = document.createElement("li");
      li.className = "autocomplete-suggestion";
      li.textContent = suggestion.label; // Use 'label' for the display text

      li.addEventListener("click", () => this.onSuggestionClick(suggestion));

      fragment.appendChild(li);
    });

    this.suggestionsContainer.appendChild(fragment);
    this.positionSuggestionsContainer();
    this.suggestionsContainer.classList.remove("visually-hidden");
  }

  private positionSuggestionsContainer(): void {
    const rect = this.input.getBoundingClientRect();
    this.suggestionsContainer.style.left = `${rect.left + window.pageXOffset}px`;
    this.suggestionsContainer.style.top = `${rect.bottom + window.pageYOffset}px`;
    this.suggestionsContainer.style.width = `${rect.width}px`;
  }

  private onSuggestionClick(suggestion: Suggestion): void {
    this.input.value = suggestion.label; // Set the text box value

    if (this.hiddenField) {
      this.hiddenField.value = suggestion.value; // Set the hidden field value
    }

    this.clearSuggestions();
  }

  private onClickOutside(event: MouseEvent): void {
    if (!this.input.contains(event.target as Node) && !this.suggestionsContainer.contains(event.target as Node)) {
      this.clearSuggestions();
    }
  }

  private clearSuggestions(): void {
    this.suggestionsContainer.innerHTML = "";
    this.suggestionsContainer.classList.add("visually-hidden");
  }
}

// Initialize the autocomplete for all inputs with "data-wf-autocomplete"
document.addEventListener("DOMContentLoaded", () => {
  const inputs = document.querySelectorAll<HTMLInputElement>('input[data-wf-autocomplete]');
  inputs.forEach((input) => {
    new Autocomplete(input);
  });
});
